import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, LayoutList, Move } from "lucide-react";
import { AddToBag, SoldOutBadge } from "@/components/AddToBag";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { BRUSHES, BRUSH_COLLECTION, BRUSH_KITS, collectionUrl, discountPct, formatINR, productUrl } from "@/data/catalog";
import { BrushDrawer } from "@/components/BrushDrawer";
import { useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const PhotoRing = lazy(() => import("@/components/three/PhotoRing"));

const Detail = ({ brush: base }) => {
  const brush = useLiveProduct(base);
  const off = discountPct(brush.price, brush.compare);
  return (
    <motion.div
      key={brush.id}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.45, ease: EASE }}
      data-testid="brush-detail"
    >
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-champagne">{brush.code} · Signature Pro Series</p>
      <h3 data-testid="brush-active-name" className="mt-3 font-display text-4xl leading-[1] text-cream sm:text-5xl">{brush.name}</h3>
      <dl className="mt-6 grid grid-cols-2 gap-6 border-t border-cream/15 pt-6">
        <div>
          <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">Best for</dt>
          <dd className="mt-1 text-sm text-cream">{brush.best}</dd>
        </div>
        <div>
          <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">Shape</dt>
          <dd className="mt-1 text-sm text-cream">{brush.shape}</dd>
        </div>
      </dl>
      <p className="mt-6 text-cream">
        <span data-testid="brush-active-price" className="font-display text-3xl">{formatINR(brush.price)}</span>
        {off > 0 && <span className="ml-3 text-sm text-stone line-through">{formatINR(brush.compare)}</span>}
        {off > 0 && <span className="ml-3 font-mono text-xs text-champagne">−{off}%</span>}
        {brush.available === false && <span className="ml-3 font-mono text-xs uppercase tracking-[0.16em] text-stone">Sold out</span>}
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <AddToBag p={brush} testId="brush-add-to-bag" className="btn-gold">Add {brush.code} to bag</AddToBag>
        <a href={productUrl(brush.handle)} data-testid="brush-view-product" className="btn-ghost">View brush</a>
      </div>
    </motion.div>
  );
};

const KitCard = ({ k: base, i }) => {
  const k = useLiveProduct(base);
  const off = discountPct(k.price, k.compare);
  return (
    <Reveal delay={(i % 4) * 0.07} className="group">
      <a href={productUrl(k.handle)} data-testid={`brush-kit-link-${k.id}`} className="relative block aspect-[16/10] overflow-hidden rounded-[2px] bg-card">
        <img src={k.img} alt={k.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
        <span className="absolute right-3 top-3 flex gap-2">
          <SoldOutBadge p={k} />
          {off > 0 && <span className="rounded-full bg-[#1f1916]/80 px-2.5 py-1 font-mono text-[11px] text-[#e6c594] backdrop-blur-md">−{off}%</span>}
        </span>
      </a>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-display text-xl leading-tight text-cream sm:text-2xl">{k.name}</h4>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.14em] text-stone">{k.note}</p>
        </div>
        <div className="shrink-0 text-right">
          <p data-testid={`brush-kit-price-${k.id}`} className="text-base text-cream">{formatINR(k.price)}</p>
          {off > 0 && <p className="text-xs text-stone line-through">{formatINR(k.compare)}</p>}
        </div>
      </div>
      <AddToBag p={k} testId={`brush-kit-add-${k.id}`} className="btn-bag mt-4" />
    </Reveal>
  );
};

export const BrushAtelier = () => {
  const [active, setActive] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const control = useRef(null);
  const strip = useRef(null);
  const brush = BRUSHES[active];

  useEffect(() => {
    const el = strip.current?.children[active];
    if (el && strip.current) strip.current.scrollTo({ left: el.offsetLeft - strip.current.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [active]);

  return (
    <section id="brushes" data-testid="brushes-section" className="theme-light relative overflow-hidden bg-ink py-24 text-cream lg:py-36">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Eyebrow>06 — WINK Signature Pro Series · {BRUSHES.length} brushes</Eyebrow>
            <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
              <MaskText lines={["Every stroke,", <em key="e" className="italic text-champagne">the right brush.</em>]} />
            </h2>
          </div>
          <Reveal className="max-w-md">
            <p className="text-sm leading-relaxed text-stone sm:text-base">
              Ultra-soft synthetic bristles, weighted black handles and a number on every ferrule so you always reach for the right tool. Spin the atelier to meet the series.
            </p>
            <a href={collectionUrl(BRUSH_COLLECTION)} data-testid="brushes-view-all" className="nav-link mt-4 inline-flex items-center gap-1 text-sm text-cream">
              Shop all brushes <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="relative min-w-0 overflow-hidden rounded-[2rem] bg-[#1f1916] lg:col-span-7" data-testid="brush-stage">
            <AnimatePresence mode="wait">
              <motion.span
                key={brush.code}
                aria-hidden
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 whitespace-nowrap font-display text-[22vw] leading-none text-[#f7f2eb]/[0.05] lg:text-[11rem]"
              >
                {brush.code}
              </motion.span>
            </AnimatePresence>
            <Suspense fallback={<div className="h-[60svh] min-h-[420px] lg:h-[640px]" />}>
              <PhotoRing
                items={BRUSHES}
                radius={5.8}
                size={[1.35, 2.36]}
                dim={0.22}
                pop={0.12}
                dragK={0.0045}
                autoDelay={3400}
                camera={{ position: [0, 1.4, 14.5], fov: 32 }}
                onActive={setActive}
                controlRef={control}
                className="h-[60svh] min-h-[420px] lg:h-[640px]"
                testId="brush-ring"
              />
            </Suspense>
            <span data-testid="brush-index" className="pointer-events-none absolute left-5 top-5 font-mono text-xs uppercase tracking-[0.25em] text-[#f7f2eb]/80">
              {String(active + 1).padStart(2, "0")} / {String(BRUSHES.length).padStart(2, "0")}
            </span>
            <div className="pointer-events-none absolute bottom-4 left-5 right-5 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#baafa7]">
                <Move className="h-3.5 w-3.5" /> <span className="sm:hidden">Swipe to spin</span><span className="hidden sm:inline">Drag to spin</span>
              </span>
              <div className="pointer-events-auto flex items-center gap-2">
                <button data-testid="brush-prev" aria-label="Previous brush" onClick={() => control.current?.prev()} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-[#f7f2eb] transition-colors hover:border-[#e6c594] hover:text-[#e6c594]"><ArrowLeft className="h-4 w-4" /></button>
                <button data-testid="brush-next" aria-label="Next brush" onClick={() => control.current?.next()} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-[#f7f2eb] transition-colors hover:border-[#e6c594] hover:text-[#e6c594]"><ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>

          <div className="flex min-w-0 flex-col justify-center lg:col-span-5">
            <AnimatePresence mode="wait">
              <Detail brush={brush} />
            </AnimatePresence>
            <div ref={strip} className="no-scrollbar -mx-5 mt-10 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:px-0" role="tablist" aria-label="Brushes" data-testid="brush-thumbs">
              {BRUSHES.map((b, i) => (
                <button
                  key={b.id}
                  role="tab"
                  aria-selected={i === active}
                  aria-label={`${b.code} ${b.name}`}
                  data-testid={`brush-thumb-${b.id}`}
                  onClick={() => control.current?.goTo(i)}
                  className={`group w-14 shrink-0 overflow-hidden rounded-md border transition-colors duration-500 sm:w-16 ${i === active ? "border-champagne" : "border-transparent hover:border-cream/30"}`}
                >
                  <img src={b.img} alt="" loading="lazy" className={`aspect-[4/7] w-full object-cover transition-opacity duration-500 ${i === active ? "opacity-100" : "opacity-60 group-hover:opacity-100"}`} />
                  <span className={`block py-1 text-center font-mono text-[9px] tracking-[0.12em] sm:text-[10px] ${i === active ? "text-champagne" : "text-stone"}`}>{b.code.replace("WSP-", "")}</span>
                </button>
              ))}
            </div>
            <button data-testid="brush-see-all" onClick={() => setDrawer(true)} className="btn-ghost mt-6 self-start">
              <LayoutList className="h-4 w-4" /> See all {BRUSHES.length} brushes
            </button>
            <BrushDrawer open={drawer} onOpenChange={setDrawer} onPick={(i) => { setDrawer(false); control.current?.goTo(i); }} />
          </div>
        </div>

        <div className="mt-20 lg:mt-28">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <h3 className="font-display text-3xl text-cream sm:text-4xl">Or take the <em className="italic text-champagne">whole kit.</em></h3>
            <a href={collectionUrl("makeup-brush-kits-and-sets")} data-testid="brush-kits-view-all" className="nav-link inline-flex items-center gap-1 text-sm text-cream">
              Kits & sets <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="brush-kits">
            {BRUSH_KITS.map((k, i) => (
              <KitCard key={k.id} k={k} i={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
